/**
 * Stock admin — REUTILIZA la lógica real de la app móvil.
 * No reimplementa FEFO, transacciones ni movimientos.
 * Copiado/adaptado de mi_funcion/reducirStock.js y aumentarStock.js
 * manteniendo el mismo modelo Firestore y el mismo aislamiento por RUC.
 */
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db, auth } from '../lib/firebase';

async function resolverProductoRef(productoId, ruc) {
  if (!ruc) throw new Error('RUC del usuario no disponible');
  if (!auth?.currentUser) throw new Error('Usuario no autenticado.');

  // Si el id ya es un doc id y existe, usarlo; si es código interno, buscar.
  const q = query(
    collection(db, 'productos'),
    where('id', '==', productoId),
    where('ruc', '==', ruc)
  );
  const snap = await getDocs(q);
  if (!snap.empty) {
    return doc(db, 'productos', snap.docs[0].id);
  }
  return doc(db, 'productos', productoId);
}

async function registrarMovimiento({ productoId, cantidad, tipo, ruc, fechaCaducidad = null, numeroLote = '' }) {
  await addDoc(collection(db, 'movimientos_stock'), {
    productoId,
    cantidad,
    tipo,
    fechaCaducidad,
    numeroLote,
    ruc,
    timestamp: serverTimestamp(),
  });
}

export async function aumentarStock({ productoId, cantidad, tipo = 'compra', fechaCaducidad = null, numeroLote = '', ruc }) {
  if (!productoId || !cantidad || cantidad <= 0 || !tipo || !ruc) {
    throw new Error('Producto, cantidad positiva, tipo y RUC son requeridos.');
  }
  const productoRef = await resolverProductoRef(productoId, ruc);

  await runTransaction(db, async (transaction) => {
    const productoSnap = await transaction.get(productoRef);
    if (!productoSnap.exists()) throw new Error('Producto no encontrado en el sistema.');
    const productoData = productoSnap.data();
    if (productoData.ruc && productoData.ruc !== ruc) {
      throw new Error('Producto no pertenece al tenant actual.');
    }

    if (productoData.controlaLotes) {
      const lotesRef = collection(productoRef, 'lotes');
      const targetLoteNum = numeroLote || 'LOTE-GENERAL';
      const loteQuery = query(lotesRef, where('numeroLote', '==', targetLoteNum));
      const loteSnap = await getDocs(loteQuery);
      if (!loteSnap.empty) {
        const loteDoc = loteSnap.docs[0];
        const loteRef = doc(productoRef, 'lotes', loteDoc.id);
        const fresh = await transaction.get(loteRef);
        const actual = fresh.exists() ? Number(fresh.data().cantidad || 0) : Number(loteDoc.data().cantidad || 0);
        transaction.update(loteRef, { cantidad: actual + cantidad });
      } else {
        const nuevoLoteRef = doc(collection(productoRef, 'lotes'));
        transaction.set(nuevoLoteRef, {
          numeroLote: targetLoteNum,
          cantidad,
          fechaCaducidad: fechaCaducidad || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        });
      }
    }

    const nuevoStock = Number(productoData.stock || 0) + cantidad;
    transaction.update(productoRef, { stock: nuevoStock });
  });

  await registrarMovimiento({
    productoId: productoRef.id,
    cantidad,
    tipo,
    ruc,
    fechaCaducidad,
    numeroLote,
  });
}

export async function reducirStock({ productoId, cantidad, tipo = 'ajuste', ruc }) {
  if (!productoId || !cantidad || cantidad <= 0 || !tipo || !ruc) {
    throw new Error('Producto, cantidad positiva, tipo y RUC son requeridos.');
  }
  const productoRef = await resolverProductoRef(productoId, ruc);

  await runTransaction(db, async (transaction) => {
    const productoSnap = await transaction.get(productoRef);
    if (!productoSnap.exists()) throw new Error('Producto no encontrado en el sistema.');
    const productoData = productoSnap.data();
    const productoNombre = productoData.name || 'Producto';
    if (productoData.ruc && productoData.ruc !== ruc) {
      throw new Error(`Producto "${productoNombre}" no pertenece al tenant actual.`);
    }

    const stockActual = Number(productoData.stock || 0);
    if (productoData.controlaStock || productoData.controlaLotes) {
      if (stockActual < cantidad) {
        throw new Error(
          `Stock insuficiente para "${productoNombre}". Stock disponible: ${stockActual}, Solicitado: ${cantidad}`
        );
      }
    }

    let cantidadRestante = cantidad;

    if (productoData.controlaLotes) {
      const lotesRef = collection(productoRef, 'lotes');
      const lotesQuery = query(lotesRef, where('cantidad', '>', 0), orderBy('fechaCaducidad', 'asc'));
      const lotesSnap = await getDocs(lotesQuery);
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      for (const loteDoc of lotesSnap.docs) {
        if (cantidadRestante <= 0) break;
        const loteData = loteDoc.data();
        if (loteData.fechaCaducidad) {
          const fecha = loteData.fechaCaducidad.toDate
            ? loteData.fechaCaducidad.toDate()
            : new Date(loteData.fechaCaducidad);
          fecha.setHours(0, 0, 0, 0);
          if (fecha < hoy) continue;
        }
        const loteRef = doc(productoRef, 'lotes', loteDoc.id);
        const fresh = await transaction.get(loteRef);
        const freshQty = fresh.exists() ? Number(fresh.data().cantidad || 0) : Number(loteData.cantidad || 0);
        if (freshQty >= cantidadRestante) {
          transaction.update(loteRef, { cantidad: freshQty - cantidadRestante });
          cantidadRestante = 0;
        } else if (freshQty > 0) {
          transaction.update(loteRef, { cantidad: 0 });
          cantidadRestante -= freshQty;
        }
      }

      if (cantidadRestante > 0) {
        throw new Error(
          `Stock en lotes no vencidos insuficiente para "${productoNombre}". Faltan ${cantidadRestante} unidades válidas.`
        );
      }
    }

    if (productoData.controlaStock || productoData.controlaLotes) {
      transaction.update(productoRef, { stock: stockActual - cantidad });
    }
  });

  await registrarMovimiento({ productoId: productoRef.id, cantidad, tipo, ruc });
}
